/**
 * Onboarding checklist permission helpers (RW-281).
 *
 * RW-733: the permission MODEL (assignee-token → role map, canToggleItem,
 * userMatchesItem, assignee normalization) now lives in ONE authored module —
 * functions/src/domain/onboardingPermissionsCore.js — consumed here via the
 * generated copy ./onboardingPermissionsCore.js (see
 * scripts/sync-onboarding-perms.mjs; CI fails on drift). This file keeps only
 * the CLIENT-side concerns: badge/filter/progress UI meta and the binding of
 * the core to the client roles module.
 *
 * Canonical vocabulary mapping (template token → platform roles allowed):
 *   "agent"          → the onboarding agent only (owner of the instance)
 *   "agent_services" → agent_services, admin, super_admin
 *   "tc"             → tc, admin, super_admin
 *   "trainer"        → trainer, admin, super_admin
 *   "broker"         → broker, admin, super_admin
 *   "admin"          → admin, super_admin
 *   "super_admin"    → super_admin only
 *
 * super_admin ALWAYS overrides every permission check.
 *
 * RW-564 dual-layer note on `agent_services` (vocabulary token vs platform
 * role) is documented in the core module.
 */

import {
  CANONICAL_ROLES,
  ACCESS_LEVELS,
  PERMANENT_SUPER_ADMIN_EMAIL,
  normalizeRoles,
  accessLevelForRoles,
  hasAnyRole,
  isElevated,
} from '../lib/roles';
import {
  ASSIGNABLE_TOKENS as CORE_ASSIGNABLE_TOKENS,
  getItemAssignees,
  isAgentItem,
  isStaffItem,
  createOnboardingPermissions,
} from './onboardingPermissionsCore';

// Bind the shared permission model to the client roles module. The server
// callable binds the same core to functions/src/util/roles.js — one model,
// two role-module twins.
const perms = createOnboardingPermissions({
  PERMANENT_SUPER_ADMIN_EMAIL,
  ACCESS_LEVELS,
  normalizeRoles,
  hasAnyRole,
  accessLevelForRoles,
  isElevated,
});

/**
 * Can `user` toggle `item` on `instance`? See the core module for the rule
 * order. Note (RW-733): the agent-own-item path is keyed on uid + instance
 * ONLY — a roleless user who owns the instance can toggle their own items,
 * matching what the server callable always enforced (RW-731 lesson).
 */
export const canToggleItem = perms.canToggleItem;

/**
 * Does `user` match any of the item's assignees? (Filter-only, not permission
 * — no super_admin override, so super_admins don't match every item in
 * "My Items".)
 */
export const userMatchesItem = perms.userMatchesItem;

export { getItemAssignees, isAgentItem, isStaffItem };

// Re-exported from the core so pickers/filters/progress and the permission
// map can never disagree about the vocabulary.
export const ASSIGNABLE_TOKENS = CORE_ASSIGNABLE_TOKENS;

// Visual meta for each assignee token (badges + filter dropdown).
export const ASSIGNEE_META = {
  agent: { label: 'Agent', color: 'coral' },
  agent_services: { label: 'Agent Services', color: 'purple' },
  tc: { label: 'TC', color: 'gray' },
  trainer: { label: 'Trainer', color: 'blue' },
  broker: { label: 'Broker', color: 'amber' },
  // RW-698: office_coordinator badge so the 10 v1 seed items render a real label.
  office_coordinator: { label: 'Office Coordinator', color: 'teal' },
  // RW-725: badges for the marketing + relo_services seed tokens (colors chosen
  // from the set the agent checklist's OB_BADGE_STYLES also supports).
  marketing: { label: 'Marketing', color: 'amber' },
  relo_services: { label: 'Relocation', color: 'blue' },
  admin: { label: 'Admin', color: 'purple' },
  super_admin: { label: 'Super Admin', color: 'red' },
  // RW-1205: staff_self (Phase 5 office-staff self-service login) joined
  // CANONICAL_ROLES without a badge here, tripping the drift guard. It is NOT
  // an assignable token (staff hires receive work via their granular staff
  // roles, not staff_self), so it stays out of ASSIGNABLE_TOKENS — this entry
  // only badges the role if it ever surfaces.
  staff_self: { label: 'Staff', color: 'gray' },
};

// Tailwind class fragments per color. Kept inline to avoid theme.js TDZ risk
// (CLAUDE.md Gotcha #11 — never reference theme imports in module constants).
const BADGE_CLASSES = {
  coral: 'bg-orange-100 text-orange-700 border-orange-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  gray: 'bg-gray-100 text-gray-700 border-gray-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  red: 'bg-red-100 text-red-700 border-red-200',
};

// Drift guard (RW-698): every canonical role must have a badge so a future
// role addition can't silently render items as unknown/unassigned. Dev-only —
// surfaces as a console warning rather than throwing in production.
if (process.env.NODE_ENV !== 'production') {
  const missing = CANONICAL_ROLES.filter((r) => !ASSIGNEE_META[r]);
  if (missing.length) {
    // eslint-disable-next-line no-console
    console.warn('[onboardingPermissions] canonical roles missing ASSIGNEE_META:', missing.join(', '));
  }
  // RW-735: every assignable token must badge — else pickers render it raw.
  const missingAssignable = ASSIGNABLE_TOKENS.filter((t) => !ASSIGNEE_META[t]);
  if (missingAssignable.length) {
    // eslint-disable-next-line no-console
    console.warn('[onboardingPermissions] ASSIGNABLE_TOKENS missing ASSIGNEE_META:', missingAssignable.join(', '));
  }
}

/**
 * Visual meta for every assignee on an item, deduped.
 * Returns [{ token, label, color, className }].
 */
export function getItemAssigneeBadges(item) {
  const tokens = getItemAssignees(item);
  const seen = new Set();
  const out = [];
  for (const token of tokens) {
    if (seen.has(token)) continue;
    seen.add(token);
    const meta = ASSIGNEE_META[token] || { label: token, color: 'gray' };
    out.push({
      token,
      label: meta.label,
      color: meta.color,
      className: BADGE_CLASSES[meta.color] || BADGE_CLASSES.gray,
    });
  }
  return out;
}

/**
 * Tooltip string for a disabled item — "Assigned to: Admin, TC".
 */
export function getAssigneeTooltip(item) {
  const badges = getItemAssigneeBadges(item);
  const labels = badges.map((b) => b.label).join(', ');
  return `Assigned to: ${labels}`;
}

/**
 * Filter options for the checklist filter dropdown.
 * 'mine' is resolved at filter-time against the current user.
 */
// RW-735: extended to cover the staff tokens added in RW-698/RW-725
// (office_coordinator, marketing, relo_services) + broker, so agents can filter
// by every role that actually appears on their checklist. Kept in sync with
// ASSIGNABLE_TOKENS (the dev drift-guard above flags divergence).
export const CHECKLIST_FILTER_OPTIONS = [
  { value: 'all', label: 'All Items' },
  { value: 'mine', label: 'My Items' },
  { value: 'agent', label: 'Agent Items' },
  { value: 'agent_services', label: 'Admin Items' },
  { value: 'tc', label: 'TC Items' },
  { value: 'trainer', label: 'Trainer Items' },
  { value: 'broker', label: 'Broker Items' },
  { value: 'office_coordinator', label: 'Office Coordinator Items' },
  { value: 'marketing', label: 'Marketing Items' },
  { value: 'relo_services', label: 'Relocation Items' },
];

/**
 * Does an item pass the given filter for the given user?
 */
export function itemMatchesFilter(item, filter, user, instance) {
  if (!filter || filter === 'all') return true;
  if (filter === 'mine') return userMatchesItem(user, item, instance);
  const assignees = getItemAssignees(item);
  return assignees.includes(filter);
}

/**
 * Per-role progress breakdown for a set of items.
 * Returns { agent: {done, total}, agent_services: {...}, tc, trainer, broker }.
 * An item counts toward EVERY role in its assignTo array (multi-assigned
 * items show up under each role). Only roles with at least one item appear.
 */
export function getProgressByRole(items) {
  const counts = {};
  (items || []).forEach((item) => {
    const assignees = getItemAssignees(item);
    const done = item.status === 'complete';
    assignees.forEach((token) => {
      if (!counts[token]) counts[token] = { done: 0, total: 0 };
      counts[token].total += 1;
      if (done) counts[token].done += 1;
    });
  });
  return counts;
}
